import { addDays, startOfDay } from 'date-fns';
import { keyBy } from 'lodash';
import { FilePlus, FileText } from 'lucide-react';

import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import { useNow } from '@/hooks/useNow';
import type { Activity } from '@/modules/documents/domain/entities/activity';
import { ActivityFileAction } from '@/modules/documents/domain/entities/activity-type';
import { DocumentType } from '@/modules/documents/domain/entities/document-type';
import { GetDocumentActivity } from '@/modules/documents/view/queries/get-document-activity';

import { NewsItem, NewsItemDescription, NewsItemIcon } from '../news-item';

import { ActivityGroupedNews } from './activity-grouped-news';
import { ActivityNews } from './activity-news';

export function NewReports() {
  const now = useNow();
  const activities =
    GetDocumentActivity.useQuery({
      fileActions: [ActivityFileAction.CREATE],
      documentType: [DocumentType.REPORTS],
      after: startOfDay(addDays(now, -7)),
    }).data ?? [];

  if (!activities.length) {
    return (
      <NewsItem>
        <NewsItemIcon>
          <FileText className="size-full text-gray-400" aria-hidden="true" />
        </NewsItemIcon>
        <NewsItemDescription isCollapsible={false}>
          No new reports generated in the last 7 days.
        </NewsItemDescription>
      </NewsItem>
    );
  }

  return (
    <NewsItem>
      <NewsItemIcon>
        <FilePlus className="size-full text-primary-600" aria-hidden="true" />
      </NewsItemIcon>
      <NewsItemDescription>
        <NewReportsLabel activities={activities} />
      </NewsItemDescription>
    </NewsItem>
  );
}

function NewReportsLabel({ activities }: { activities: Activity[] }) {
  const assets = GetAllSites.useQuery();

  const sitesById = keyBy(assets.data, 'id');

  if (activities.length === 1) {
    return (
      <>
        A new report has been generated:{' '}
        <ActivityNews activity={activities[0]} />
      </>
    );
  }

  const { globalActivities, activitiesPerSiteId } = activities.reduce(
    (acc, activity) => {
      if (activity.siteIds.length === 0) {
        acc.globalActivities.push(activity);
      } else {
        for (const siteId of activity.siteIds) {
          if (!acc.activitiesPerSiteId.has(siteId)) {
            acc.activitiesPerSiteId.set(siteId, []);
          }
          acc.activitiesPerSiteId.get(siteId)!.push(activity);
        }
      }
      return acc;
    },
    {
      globalActivities: [] as Activity[],
      activitiesPerSiteId: new Map<string, Activity[]>(),
    },
  );

  return (
    <>
      New reports have been generated:{' '}
      <ol className="mt-1 list-disc">
        {globalActivities.map(function (activity) {
          return (
            <li key={activity.id}>
              <ActivityNews activity={activity} />
            </li>
          );
        })}
        {Array.from(activitiesPerSiteId).map(([siteId, activities]) => (
          <li key={siteId}>
            <ActivityGroupedNews
              isLoading={assets.isLoading}
              site={sitesById[siteId]}
              activities={activities}
            />
          </li>
        ))}
      </ol>
    </>
  );
}
